import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react'
import { useSelector } from 'react-redux'
import { Modal } from 'reactstrap'

import { BaseScheduleFormData, BaseScheduleIndex } from '../../../../@paco/entities/BaseSchedule/BaseSchedule'
import checkPermission from '../../../../@paco/helpers/permissions/checkPermission'
import trans from '../../../../@paco/helpers/trans'
import {
    deleteBaseSchedule,
    editBaseSchedule,
    fetchBaseSchedules,
} from '../../../../@paco/redux/baseSchedule/baseScheduleActions'
import { setIsEditSuccessful } from '../../../../@paco/redux/baseSchedule/baseScheduleReducer'
import { useTypedDispatch, useTypedSelector } from '../../../../@paco/redux/store'
import { setToast } from '../../../../@paco/redux/toasts/toastsReducer'
import { ToastType } from '../../../../@paco/types'

import DeleteResourceForm from '../../../../components/forms/DeleteResourceForm/DeleteResourceForm'
import { translate } from '../../../../helpers/translations/translator'
import { Reducers } from '../../../../redux/reducers'
import BaseSchedulesTable from '../../containers/BaseSchedules/BaseSchedulesTable/BaseSchedulesTable'
import BaseScheduleForm from '../../forms/BaseScheduleForm/BaseScheduleForm'

const ConnectedBaseSchedules: FC = (): ReactElement => {
    const dispatch = useTypedDispatch()

    const {
        baseSchedules,
        isLoading,
        isEditSuccessful,
    } = useTypedSelector(state => state.pacoBaseScheduleReducer)
    const filter = useSelector((state: Reducers) => state.filterReducer.filter)

    const [deleteBaseScheduleForm, setDeleteBaseScheduleForm] = useState(false)
    const [editBaseScheduleForm, setEditBaseScheduleForm] = useState(false)
    const [activeBaseSchedule, setActiveBaseSchedule] = useState<BaseScheduleIndex | undefined>()
    const { permissions } = useTypedSelector(state => state.authenticatedUserReducer)

    const canEditBaseSchedules = checkPermission(permissions, 'edit-all-base-schedules')
    const canViewBaseScheduleShifts = checkPermission(permissions, 'view-all-base-schedule-shifts')
    const canDeleteBaseSchedules = checkPermission(permissions, 'delete-all-base-schedules')

    const handleEditBaseScheduleButtonClick = (baseSchedule: BaseScheduleIndex) => {
        setEditBaseScheduleForm(true)
        setActiveBaseSchedule(baseSchedule)
    }

    const handleDeleteBaseScheduleButtonClick = (baseSchedule: BaseScheduleIndex) => {
        setDeleteBaseScheduleForm(true)
        setActiveBaseSchedule(baseSchedule)
    }

    const handleSubmitDeleteBaseScheduleForm = () => {
        if (activeBaseSchedule) {
            dispatch(deleteBaseSchedule(activeBaseSchedule.id))
        }
        setDeleteBaseScheduleForm(false)
    }

    const handleCancelDeleteBaseScheduleForm = () => {
        setDeleteBaseScheduleForm(false)
    }

    const handleSubmitEditBaseSchedule = (formData: BaseScheduleFormData) => {
        if (activeBaseSchedule) {
            dispatch(editBaseSchedule(activeBaseSchedule.id, formData))
        }
        setEditBaseScheduleForm(false)
    }

    const handleCancelEditBaseSchedule = () => {
        setEditBaseScheduleForm(false)
    }

    const getBaseSchedules = (): void => {
        dispatch(fetchBaseSchedules(filter))
    }

    useEffect((): void => {
        getBaseSchedules()
    }, [filter.departmentOptions])

    useEffect((): void => {
        if (isEditSuccessful) {
            dispatch(setIsEditSuccessful(false))

            dispatch(setToast({
                text: trans('containers.baseSchedulesTable.editSuccessful'),
                type: ToastType.pass,
            }))
        }
    }, [isEditSuccessful])

    return (
        <>
            <BaseSchedulesTable
                isLoading={isLoading}
                baseSchedules={baseSchedules}
                canEditBaseSchedules={canEditBaseSchedules}
                canViewBaseScheduleShifts={canViewBaseScheduleShifts}
                canDeleteBaseSchedules={canDeleteBaseSchedules}
                onEditBaseScheduleButtonClick={handleEditBaseScheduleButtonClick}
                onDeleteBaseScheduleButtonClick={handleDeleteBaseScheduleButtonClick}
            />
            {canDeleteBaseSchedules && (
                <Modal
                    size="md"
                    isOpen={deleteBaseScheduleForm}
                >
                    <DeleteResourceForm
                        title={translate('common.deleteResource', { resource: translate('common.baseSchedule') })}
                        intro={translate('common.deleteResourceIntro', { resource: translate('common.baseSchedule').toLowerCase() })}
                        onSubmit={handleSubmitDeleteBaseScheduleForm}
                        onCancel={handleCancelDeleteBaseScheduleForm}
                    />
                </Modal>
            )}
            {canEditBaseSchedules && (
                <Modal
                    size="md"
                    isOpen={editBaseScheduleForm}
                >
                    <BaseScheduleForm
                        title={translate('common.editEntity', { entity: translate('common.baseSchedule') })}
                        baseSchedule={activeBaseSchedule}
                        onSubmit={handleSubmitEditBaseSchedule}
                        onCancel={handleCancelEditBaseSchedule}
                    />
                </Modal>
            )}
        </>
    )
}

export default ConnectedBaseSchedules
